import { Permissions } from "@cnx/common";
import {
  IconAlertCircle,
  IconBulb,
  IconHome2,
  IconTruck,
  IconUser,
} from "@tabler/icons-react";
import FromShipIcon from "assets/icons/shipment/from-ship.svg";
import ToShipIcon from "assets/icons/shipment/to-ship.svg";
import IconUnclearShipment from "icons/shipment/unclear-shipment.svg";

import {
  FEATURE_TERMINAL_MANAGEMENT,
  FEATURE_TERMINAL_UNCLEAR_SHIPMENTS,
} from "common/constants";
import { UnclearShipmentsBadge } from "components/TerminalManagement";
import { useFeatureSupport } from "hooks/useFeatureSupport";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useAuthStore } from "stores/authStore";
import { Link } from "types/Link";

export const useMainMenuLinks = () => {
  const { t } = useTranslation();
  const useRights = new Set(useAuthStore(state => state.useRights));
  const { isFeatureEnabled } = useFeatureSupport();
  const { pathname: currentPath } = useLocation();

  const isTerminalUser =
    useRights.has(Permissions.RIGHTS_TERMINAL_USER) ||
    useRights.has(Permissions.RIGHTS_TERMINAL_ADMIN);

  const mainMenuLinks: Link[] = [
    {
      isActive: currentPath === "/home",
      link: "/home",
      icon: <IconHome2 />,
      label: t("home") as string,
    },
  ];

  (useRights.has(Permissions.RIGHTS_SHIPMENTS_USER) ||
    useRights.has(Permissions.RIGHTS_SHIPMENTS_ADMIN)) &&
    mainMenuLinks.push({
      isActive: currentPath.startsWith("/trips"),
      link: "/trips",
      icon: <IconTruck />,
      label: t("shipments") as string,
    });
  (useRights.has(Permissions.RIGHTS_USERS_USER) ||
    useRights.has(Permissions.RIGHTS_USERS_ADMIN)) &&
    mainMenuLinks.push({
      isActive: currentPath === "/users",
      link: "/users",
      icon: <IconUser />,
      label: t("users"),
    });
  (useRights.has(Permissions.RIGHTS_USER_STORIES_USER) ||
    useRights.has(Permissions.RIGHTS_USER_STORIES_ADMIN)) &&
    mainMenuLinks.push({
      isActive: currentPath === "/epics",
      link: "/epics",
      icon: <IconBulb />,
      label: t("ideas"),
    });
  useRights.has(Permissions.RIGHTS_EXCEPTIONS_ADMIN) &&
    mainMenuLinks.push({
      isActive: currentPath === "/exceptions",
      link: "/exceptions",
      icon: <IconAlertCircle />,
      label: t("damages"),
    });
  (useRights.has(Permissions.RIGHTS_TERMINAL_USER) ||
    useRights.has(Permissions.RIGHTS_TERMINAL_ADMIN)) &&
    isFeatureEnabled(FEATURE_TERMINAL_MANAGEMENT) &&
    mainMenuLinks.push({
      isActive: currentPath.startsWith("/terminal-management/arrivals"),
      link: "/terminal-management/arrivals",
      icon: <FromShipIcon className="w-[24px] h-[24px]" />,
      label: t("terminalArrivals"),
    });

  (useRights.has(Permissions.RIGHTS_TERMINAL_USER) ||
    useRights.has(Permissions.RIGHTS_TERMINAL_ADMIN)) &&
    isFeatureEnabled(FEATURE_TERMINAL_MANAGEMENT) &&
    mainMenuLinks.push({
      isActive: currentPath.startsWith("/terminal-management/departures"),
      link: "/terminal-management/departures",
      icon: <ToShipIcon className="w-[24px] h-[24px]" />,
      label: t("terminalDepartures"),
    });

  isTerminalUser &&
    isFeatureEnabled(FEATURE_TERMINAL_UNCLEAR_SHIPMENTS) &&
    mainMenuLinks.push({
      isActive: currentPath.startsWith(
        "/terminal-management/unclear-shipments"
      ),
      link: "/terminal-management/unclear-shipments",
      icon: <IconUnclearShipment className="w-[24px] h-[24px]" />,
      label: t("terminalUnclearShipments"),
      badge: <UnclearShipmentsBadge />,
    });

  return mainMenuLinks;
};
